import { Alert, Button, Card, Descriptions, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type ExportRow = {
  id: string;
  admission_no: string;
  validation_status: string;
  validation_messages?: string | null;
  payload?: Record<string, string | null>;
};

type Run = {
  id: string;
  run_no: string;
  title: string;
  status: string;
  student_count: number;
  error_count: number;
  warning_count: number;
  summary?: string | null;
  session_name?: string;
  session_code?: string;
  campus_name?: string;
  udise_school_code?: string | null;
  rows: ExportRow[];
};

export default function UdiseRunDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.udise.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-udise-run', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Run>(`/education/udise/runs/${id}`)).data,
  });
  const row = query.data;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-udise-run', id] });
    queryClient.invalidateQueries({ queryKey: ['education-udise-runs'] });
  };

  const validate = useMutation({
    mutationFn: () => api.post(`/education/udise/runs/${id}/validate`),
    onSuccess: () => {
      message.success(t('udiseRunValidated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const markExported = useMutation({
    mutationFn: () => api.post(`/education/udise/runs/${id}/export`),
    onSuccess: () => {
      message.success(t('udiseRunExported'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const cancel = useMutation({
    mutationFn: () => api.post(`/education/udise/runs/${id}/cancel`),
    onSuccess: () => {
      message.success(t('udiseRunCancelled'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const downloadPackage = async () => {
    try {
      const pkg = (await api.get(`/education/udise/runs/${id}/package`)).data;
      const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row?.run_no || 'udise'}-package.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message || t('unavailable', { status: '' }));
    }
  };

  return (
    <EducationPageChrome
      title={row?.run_no || t('udiseRuns')}
      description={t('udiseRunsIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/udise/runs')}>{t('udiseRuns')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('udiseReadinessHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('title')}>{row.title}</Descriptions.Item>
              <Descriptions.Item label={t('session')}>
                {row.session_name || row.session_code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('campus')}>{row.campus_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('udiseSchoolCode')}>
                {row.udise_school_code || '—'}
              </Descriptions.Item>
              <Descriptions.Item label={t('students')}>{row.student_count}</Descriptions.Item>
              <Descriptions.Item label={t('errors')}>
                {row.error_count} / {t('warnings')}: {row.warning_count}
              </Descriptions.Item>
              <Descriptions.Item label={t('summary')} span={2}>
                {row.summary || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
          {canManage && row && row.status !== 'exported' && row.status !== 'cancelled' ? (
            <Space style={{ marginTop: 12 }} wrap>
              <Button type="primary" loading={validate.isPending} onClick={() => validate.mutate()}>
                {t('validateUdiseRun')}
              </Button>
              {row.status === 'validated' ? (
                <Button loading={markExported.isPending} onClick={() => markExported.mutate()}>
                  {t('markUdiseExported')}
                </Button>
              ) : null}
              <Button onClick={() => downloadPackage()}>{t('downloadUdisePackage')}</Button>
              <Button danger loading={cancel.isPending} onClick={() => cancel.mutate()}>
                {t('cancel')}
              </Button>
            </Space>
          ) : null}
          {row && (row.status === 'validated' || row.status === 'exported') ? (
            <Space style={{ marginTop: 12 }}>
              <Button onClick={() => downloadPackage()}>{t('downloadUdisePackage')}</Button>
            </Space>
          ) : null}
        </Card>
        <Card size="small" title={t('udiseRows')}>
          <Table
            rowKey="id"
            size="small"
            pagination={{ pageSize: 50 }}
            dataSource={row?.rows ?? []}
            columns={[
              { title: t('admissionNo'), dataIndex: 'admission_no', width: 120 },
              {
                title: t('status'),
                dataIndex: 'validation_status',
                width: 100,
                render: (v: string) => (
                  <Tag color={v === 'error' ? 'red' : v === 'warning' ? 'orange' : 'green'}>{v}</Tag>
                ),
              },
              {
                title: t('validationMessages'),
                dataIndex: 'validation_messages',
                render: (v: string | null | undefined) => v || '—',
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
